import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Sparkles,
  Send,
  Trash2,
  Database,
  Sun,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Bot,
  User
} from 'lucide-react';
import { aiAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Theme colors
const THEME = {
  primary: '#679632',
  primaryLight: '#76AB3F',
  primaryBg: 'rgba(103, 150, 50, 0.1)',
  primaryBgHover: 'rgba(103, 150, 50, 0.15)',
};

// Agent badge config
const AGENT_LABELS = {
  database: { label: 'Database Agent', icon: Database, bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  solar: { label: 'Solar News Agent', icon: Sun, bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  general: { label: 'General Assistant', icon: MessageSquare, bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
  error: { label: 'Error', icon: AlertCircle, bgColor: 'bg-red-100', textColor: 'text-red-700' }
};

// Simple markdown renderer for AI responses
function renderMarkdown(text) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let listKey = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${listKey++}`} className="list-disc list-inside space-y-1 my-2">
          {listItems.map((item, i) => (
            <li key={i} className="text-sm text-gray-700">{formatInline(item)}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  const formatInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('### ')) {
      flushList();
      elements.push(
        <h4 key={`h-${i}`} className="font-semibold text-gray-800 mt-3 mb-1 text-sm">
          {formatInline(line.slice(4))}
        </h4>
      );
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(
        <h3 key={`h-${i}`} className="font-semibold text-gray-800 mt-3 mb-1">
          {formatInline(line.slice(3))}
        </h3>
      );
    } else if (line.startsWith('# ')) {
      flushList();
      elements.push(
        <h2 key={`h-${i}`} className="font-bold text-gray-900 mt-3 mb-1 text-lg">
          {formatInline(line.slice(2))}
        </h2>
      );
    } else if (line.match(/^[-*]\s/)) {
      listItems.push(line.replace(/^[-*]\s/, ''));
    } else if (line.match(/^\d+\.\s/)) {
      listItems.push(line.replace(/^\d+\.\s/, ''));
    } else if (line.trim() === '') {
      flushList();
      elements.push(<div key={`br-${i}`} className="h-2" />);
    } else {
      flushList();
      elements.push(
        <p key={`p-${i}`} className="text-sm text-gray-700 my-1">
          {formatInline(line)}
        </p>
      );
    }
  }
  flushList();

  return elements;
}

// Data table component for structured results
function DataTable({ data }) {
  const [expanded, setExpanded] = useState(false);

  if (!data || (Array.isArray(data) && data.length === 0)) return null;
  if (data.count !== undefined && !Array.isArray(data)) return null;
  if (!Array.isArray(data) || data.length === 0) return null;

  const displayData = expanded ? data : data.slice(0, 5);
  const columns = Object.keys(data[0]).filter(
    col => !['id', 'created_at', 'updated_at', 'company_group_id'].includes(col)
  );
  const visibleColumns = columns.slice(0, 6);

  return (
    <div className="mt-3">
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50">
            <tr>
              {visibleColumns.map(col => (
                <th key={col} className="px-3 py-2 text-left font-medium text-gray-600 uppercase tracking-wider">
                  {col.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {displayData.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {visibleColumns.map(col => (
                  <td key={col} className="px-3 py-2 text-gray-700 max-w-[200px] truncate">
                    {row[col] === null || row[col] === undefined
                      ? '-'
                      : typeof row[col] === 'boolean'
                        ? (row[col] ? 'Yes' : 'No')
                        : String(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 flex items-center gap-1 text-xs font-medium hover:opacity-80"
          style={{ color: THEME.primary }}
        >
          {expanded ? (
            <>Show less <ChevronUp size={14} /></>
          ) : (
            <>Show all {data.length} rows <ChevronDown size={14} /></>
          )}
        </button>
      )}
    </div>
  );
}

// Typing indicator
function TypingIndicator() {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: THEME.primaryBg }}>
        <Bot size={16} style={{ color: THEME.primary }} />
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: THEME.primary, animationDelay: '0ms' }} />
          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: THEME.primary, animationDelay: '150ms' }} />
          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: THEME.primary, animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

// Follow-up suggestion chips
function FollowUpChips({ suggestions, onSelect, disabled }) {
  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 ml-11 mt-2 mb-2">
      {suggestions.map((suggestion, i) => (
        <button
          key={i}
          onClick={() => onSelect(suggestion)}
          disabled={disabled}
          className="px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-full text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            borderColor: disabled ? undefined : undefined,
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              e.target.style.borderColor = THEME.primary;
              e.target.style.color = THEME.primary;
              e.target.style.backgroundColor = THEME.primaryBg;
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = '';
            e.target.style.color = '';
            e.target.style.backgroundColor = '';
          }}
        >
          {suggestion}
        </button>
      ))}
    </div>
  );
}

export default function AiAssistant() {
  const { user } = useAuth();
  // Chat history is persisted in sessionStorage (keyed per user) so switching
  // tabs keeps the conversation visible. sessionStorage clears on browser close,
  // and AuthContext.logout() clears the ai-chat:* keys on sign-out.
  const storageKey = `ai-chat:${user?.id || user?.email || 'anon'}`;
  const loadSavedMessages = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  };

  const [messages, setMessages] = useState(loadSavedMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Persist messages on every change; drop when empty.
  useEffect(() => {
    try {
      if (messages.length === 0) sessionStorage.removeItem(storageKey);
      else sessionStorage.setItem(storageKey, JSON.stringify(messages));
    } catch { /* quota / disabled — non-fatal */ }
  }, [messages, storageKey]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  // Load suggestions on mount
  useEffect(() => {
    let cancelled = false;
    async function loadSuggestions() {
      try {
        const response = await aiAPI.getSuggestions();
        if (!cancelled && response.success) {
          setSuggestions(response.suggestions);
        }
      } catch {
        if (!cancelled) {
          setSuggestions([
            "How many leads were identified today?",
            "What's the current pipeline status?",
            "How many clients are in potential?",
            "Which BDE has the most leads?",
            "Show me high priority leads",
            "What are the latest solar industry trends?"
          ]);
        }
      } finally {
        if (!cancelled) setSuggestionsLoading(false);
      }
    }
    loadSuggestions();
    return () => { cancelled = true; };
  }, []);

  const sendMessage = async (questionText) => {
    const question = (questionText || input).trim();
    if (!question || loading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: question,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Send the last few turns as history so the backend can answer follow-ups from
      // the conversation context ("their emails", "just the Mexico one") without
      // re-running the DB planner/query. Keep it tight to limit token cost:
      //   * last 6 messages,
      //   * data rows clipped to the most recent assistant turn and capped at 30 rows.
      const historyWindow = messages.slice(-6);
      const lastAssistantWithData = [...historyWindow].reverse().find(
        m => m.role === 'assistant' && Array.isArray(m.data) && m.data.length > 0
      );
      const conversationHistory = historyWindow.map(m => {
        const entry = { role: m.role, content: m.content };
        if (m === lastAssistantWithData) {
          entry.data = m.data.slice(0, 30);
        }
        return entry;
      });

      const response = await aiAPI.chat(question, conversationHistory);

      const aiMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.answer || 'No response received.',
        data: response.data,
        agent: response.agent || response.source || 'general',
        followUpSuggestions: response.followUpSuggestions || [],
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI Chat error:', error);

      // Extract the most useful error message
      let errorText = 'Something went wrong. Please try again.';
      let errorFollowUps = [];

      if (error.response?.data) {
        errorText = error.response.data.error || error.response.data.message || errorText;
        errorFollowUps = error.response.data.followUpSuggestions || [];
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorText = 'The AI is taking too long to respond. Please try a simpler question or try again.';
      } else if (error.message?.includes('Network Error')) {
        errorText = 'Unable to reach the server. Please make sure the backend is running and try again.';
      } else if (error.message) {
        errorText = error.message;
      }

      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: errorText,
        agent: 'error',
        followUpSuggestions: errorFollowUps.length > 0 ? errorFollowUps : [
          "How many leads today?",
          "Show pipeline status",
          "What are the latest solar trends?"
        ],
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 10rem)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: THEME.primaryBg }}>
            <Sparkles className="w-5 h-5" style={{ color: THEME.primary }} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-800">AI Assistant</h1>
            <p className="text-xs text-gray-500">Ask about your sales data, pipeline, or solar industry news</p>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearChat}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Clear conversation"
          >
            <Trash2 size={14} />
            Clear
          </button>
        )}
      </div>

      {/* Chat Area - Full width */}
      <div className="flex-1 overflow-y-auto bg-white rounded-lg shadow-md border border-gray-200 p-6 space-y-4 mb-4">
        {/* Welcome state with suggestions */}
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: THEME.primaryBg }}>
              <Sparkles className="w-10 h-10" style={{ color: THEME.primary }} />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              How can I help you today?
            </h2>
            <p className="text-sm text-gray-500 mb-8 max-w-lg">
              I can query your sales data, provide pipeline analytics, and share solar industry insights.
            </p>

            {/* Suggestion chips */}
            {!suggestionsLoading && suggestions.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2 max-w-3xl">
                {suggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(suggestion)}
                    className="px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl text-gray-700 hover:shadow-sm transition-all shadow-sm"
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = THEME.primary;
                      e.target.style.color = THEME.primary;
                      e.target.style.backgroundColor = THEME.primaryBg;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '';
                      e.target.style.color = '';
                      e.target.style.backgroundColor = '';
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        {messages.map((message, idx) => {
          if (message.role === 'user') {
            return (
              <div key={message.id} className="flex items-start gap-3 justify-end">
                <div className="rounded-2xl rounded-tr-sm px-4 py-3 max-w-[75%] shadow-sm text-white" style={{ backgroundColor: THEME.primary }}>
                  <p className="text-sm">{message.content}</p>
                </div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: THEME.primary }}>
                  <User size={16} className="text-white" />
                </div>
              </div>
            );
          }

          // Assistant message
          const agentInfo = AGENT_LABELS[message.agent] || AGENT_LABELS.general;
          const AgentIcon = agentInfo.icon;
          return (
            <div key={message.id}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: THEME.primaryBg }}>
                  <Bot size={16} style={{ color: THEME.primary }} />
                </div>
                <div className="max-w-[85%]">
                  {/* Agent badge */}
                  <div className="mb-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${agentInfo.bgColor} ${agentInfo.textColor}`}>
                      <AgentIcon size={12} />
                      {agentInfo.label}
                    </span>
                  </div>

                  {/* Message content */}
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                    <div className="prose prose-sm max-w-none">
                      {renderMarkdown(message.content)}
                    </div>
                    {message.data && <DataTable data={message.data} />}
                  </div>
                </div>
              </div>

              {/* Follow-up suggestion chips - always show after AI responses */}
              {message.followUpSuggestions && message.followUpSuggestions.length > 0 && (
                <FollowUpChips
                  suggestions={message.followUpSuggestions}
                  onSelect={sendMessage}
                  disabled={loading}
                />
              )}
            </div>
          );
        })}

        {/* Typing indicator */}
        {loading && <TypingIndicator />}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area - Full width */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about leads, pipeline, BDE performance, solar news..."
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 text-sm shadow-sm"
            style={{ focusRingColor: THEME.primary }}
            onFocus={(e) => {
              e.target.style.boxShadow = `0 0 0 2px ${THEME.primary}40`;
              e.target.style.borderColor = THEME.primary;
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = '';
              e.target.style.borderColor = '';
            }}
            aria-label="Ask the AI assistant a question"
          />
        </div>
        <button
          onClick={() => sendMessage()}
          disabled={loading || !input.trim()}
          className="px-5 py-3 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium shadow-sm"
          style={{ backgroundColor: THEME.primary }}
          onMouseEnter={(e) => { if (!e.target.disabled) e.target.style.backgroundColor = '#5a8a2d'; }}
          onMouseLeave={(e) => { e.target.style.backgroundColor = THEME.primary; }}
          aria-label="Send message"
        >
          <Send size={16} />
          Send
        </button>
      </div>
    </div>
  );
}
